import { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Checkbox, Row, Typography } from '@tempmee/react';
import { Icon } from '~/commonComponents/Icon';
import { SmartTooltip } from '~/commonComponents/SmartTooltip/SmartTooltip';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useAppSelector } from '~/hooks/useAppSelector';
import { type IBadge } from '~/domain/RecommendedProfessionals/recommendedProfessionalsTypes';
import { type IFavoriteInvite, type IJob } from '~/models';
import defaultProfileImage from '~/images/defaultProfileImage.png';
import { colors } from '@tempmee/tailwind';
import { EmbeddedRatingPopover } from './EmbeddedRatingPopover';

const ATTENDANCE_BADGE_TYPE = 'attendance-score';
const AVATAR_SIZE = 48;
const CHECKBOX_SIZE = 18;

interface IEmbeddedListItemProps {
  id: string;
  fullName: string;
  isFavorite: boolean;
  isAvailable: boolean;
  isSelected: boolean;
  profileImage: string;
  rating?: {
    averageRating: number;
    professionalSkills?: number;
    interpersonalSkills?: number;
    timeManagement?: number;
  };
  badges: IBadge[];
  onItemClick: (id: string) => void;
  onViewSkills: () => void;
}

interface IBadgeButtonProps {
  badge: IBadge;
  onShowPopup: () => void;
}

const BadgeButton = ({ badge, onShowPopup }: IBadgeButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <SmartTooltip
      content={badge.label}
      position="bottom"
      alignment="center"
      trigger="hover"
      className="smart-tooltip"
    >
      <Box
        as="span"
        role="button"
        aria-label={badge.label}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          onShowPopup();
        }}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: isHovered ? 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))' : 'none',
          transition: 'filter 0.2s',
        }}
      >
        <Box
          as="span"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundImage: `url(${badge.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'block',
          }}
          aria-label={badge.label}
        />
      </Box>
    </SmartTooltip>
  );
};

export const EmbeddedListItem = ({
  id,
  fullName,
  isFavorite,
  isAvailable,
  isSelected,
  profileImage,
  rating,
  badges,
  onItemClick,
  onViewSkills,
}: IEmbeddedListItemProps) => {
  const [showBadgePopup, setShowBadgePopup] = useState(false);

  const isInviteSent = useAppSelector((state) =>
    (state.job.job as IJob)?.favoriteInvites?.some(
      (it: IFavoriteInvite) => (it?.professional as any) === id,
    ),
  );

  const attendanceBadge = badges.find((b) => b.type === ATTENDANCE_BADGE_TYPE);

  const handleClick = () => {
    if (!isInviteSent) onItemClick(id);
  };

  const handleViewSkills = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewSkills();
  };

  return (
    <>
      <motion.div
        role="presentation"
        layout
        onClick={handleClick}
        data-testid="embedded-list-item"
        data-cy={`invite-row-${id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderBottom: `1px solid ${colors.neutral[100]}`,
          cursor: isInviteSent ? 'auto' : 'pointer',
        }}
      >
        <Row align="center" gap={2} style={{ flex: 1, minWidth: 0 }}>
          {!isInviteSent && (
            <Box
              onClick={(e) => e.stopPropagation()}
              style={{
                flexShrink: 0,
                lineHeight: 0,
                width: CHECKBOX_SIZE,
                height: CHECKBOX_SIZE,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => onItemClick(id)}
                size="sm"
              />
            </Box>
          )}

          <Box
            style={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              flexShrink: 0,
              borderRadius: '50%',
              border: isFavorite ? `2px solid ${colors.favorites[400]}` : '2px solid transparent',
              padding: isFavorite ? 2 : 0,
              boxSizing: 'border-box',
            }}
          >
            <Box
              as="span"
              role="img"
              aria-label="profile"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                backgroundImage: `url(${profileImage || defaultProfileImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: colors.neutral[100],
              }}
            />
          </Box>

          <Row align="center" gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Typography
              bold
              color={colors.neutral[600]}
              style={{
                fontSize: 18,
                maxWidth: 290,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fullName}
            </Typography>
            {isAvailable ? (
              <>
                <Typography color={colors.neutral[500]} style={{ fontSize: 16 }}>
                  {'•'}
                </Typography>
                <Typography
                  bold
                  color={colors.secondary[500]}
                  onClick={handleViewSkills}
                  data-cy={`invite-view-skills-${id}`}
                  style={{ fontSize: 18, cursor: 'pointer' }}
                >
                  View Skills
                </Typography>
              </>
            ) : (
              <Typography color={colors.error[500]} style={{ fontSize: 16 }}>
                Currently unavailable
              </Typography>
            )}
          </Row>
        </Row>

        <Row align="center" gap={3} style={{ flexShrink: 0 }}>
          {isInviteSent ? (
            <Row align="center" gap={1}>
              <Icon name="check-sent" size={24} />
              <Typography color={colors.neutral[400]} style={{ fontSize: 14 }}>
                Sent
              </Typography>
            </Row>
          ) : (
            <>
              {rating && (
                <EmbeddedRatingPopover
                  averageRating={rating.averageRating}
                  professionalSkills={rating.professionalSkills}
                  interpersonalSkills={rating.interpersonalSkills}
                  timeManagement={rating.timeManagement}
                />
              )}
              {attendanceBadge && (
                <BadgeButton badge={attendanceBadge} onShowPopup={() => setShowBadgePopup(true)} />
              )}
            </>
          )}
        </Row>
      </motion.div>

      {showBadgePopup && attendanceBadge && (
        // @ts-expect-error ConfirmPopup is JS
        <ConfirmPopup
          rightButtonAction={() => setShowBadgePopup(false)}
          rightButtonText="Got it!"
          title={
            <Box
              as="span"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: 18,
              }}
            >
              <Typography
                as="span"
                bold
                color={colors.neutral[600]}
                style={{ fontSize: 18, marginRight: 5 }}
              >
                {attendanceBadge.popupTitle}
              </Typography>
              {attendanceBadge.popupTitleIcon && (
                <Box
                  as="span"
                  role="img"
                  aria-label="icon"
                  style={{
                    width: 22,
                    height: 22,
                    backgroundImage: `url(${attendanceBadge.popupTitleIcon})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )}
            </Box>
          }
          titleStyle={{ marginBottom: 5 }}
          description={attendanceBadge.popupDescription}
          Icon={() =>
            attendanceBadge.popupImageWeb ? (
              <Box
                as="span"
                role="img"
                aria-label="popup"
                style={{
                  display: 'block',
                  width: '50%',
                  aspectRatio: '1 / 1',
                  backgroundImage: `url(${attendanceBadge.popupImageWeb})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  marginInline: 'auto',
                }}
              />
            ) : null
          }
        />
      )}
    </>
  );
};
